import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { MissionIcon, Building, PersonIcon, CloseModal, Loading2, SearchLoop, Star } from 'assets/icons';
import AdminSearchInput from 'components/AdminSearch/AdminSearchInput';
import useSlideOver from 'components/SlideOver/useSlideOver';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';
import CommandPaletteItem from './CommandPaletteItem';
import { buildMissionDetailHref } from 'helper/missionNavigation';
import { buildMatchPresentation, highlightSlot } from 'utils/searchResultHighlight';

// Fonction pour formater la date de création de manière relative
const formatCreatedDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const time = `${hours}:${minutes}`;
    
    if (dateDay.getTime() === today.getTime()) {
        return `Créé aujourd'hui à ${time}`;
    } else if (dateDay.getTime() === yesterday.getTime()) {
        return `Créé hier à ${time}`;
    } else {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear().toString().slice(-2);
        return `Créé le ${day}/${month}/${year} à ${time}`;
    }
};

const formatAssignedTempWorker = (mission) => {
    const assignedUser = mission.user || mission.user_assigned;
    if (!assignedUser) return '';

    const fullName = `${assignedUser.firstname || ''} ${assignedUser.lastname || ''}`.trim();
    if (!fullName && !assignedUser.id_bestt) return '';

    return `Assigné à ${fullName}${assignedUser.id_bestt ? ` n°${assignedUser.id_bestt}` : ''}`;
};

const formatClientContactCompanies = (contact) =>
    (contact.companies || [])
        .map((company) => company.name_commercial || company.reason_social)
        .filter(Boolean)
        .join(' · ');

const EMPTY_RESULTS = { missions: [], companies: [], clientContacts: [], tempWorkers: [] };

const TABS = [
    { id: 'all', label: 'Tous' },
    { id: 'missions', label: 'Missions' },
    { id: 'companies', label: 'Entreprises' },
    { id: 'clientContacts', label: 'Contact client' },
    { id: 'tempWorkers', label: 'Candidats' },
];

const CommandPalette = ({ isOpen, onClose }) => {
    const [search, setSearch] = useState('');
    const [results, setResults] = useState(EMPTY_RESULTS);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [activeTab, setActiveTab] = useState('all');
    const inputRef = useRef(null);
    const resultsRef = useRef(null);
    const navigate = useNavigate();
    const { toggleSlideOver } = useSlideOver();
    const { setObjID, setTarget } = UseContext();
    const token = localStorage.getItem('xsrfToken');

    // Filtrer les résultats selon le tab actif
    const filteredResults = {
        missions: activeTab === 'all' || activeTab === 'missions' ? results.missions : [],
        companies: activeTab === 'all' || activeTab === 'companies' ? results.companies : [],
        clientContacts: activeTab === 'all' || activeTab === 'clientContacts' ? results.clientContacts : [],
        tempWorkers: activeTab === 'all' || activeTab === 'tempWorkers' ? results.tempWorkers : [],
    };

    // Flatten all results for keyboard navigation
    const allResults = [
        ...filteredResults.missions.map(m => ({ ...m, type: 'mission' })),
        ...filteredResults.companies.map(c => ({ ...c, type: 'company' })),
        ...filteredResults.clientContacts.map(c => ({ ...c, type: 'clientContact' })),
        ...filteredResults.tempWorkers.map(t => ({ ...t, type: 'tempWorker' }))
    ];

    // Focus input when opened, clear cache when closed
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
            setSearch('');
            setResults(EMPTY_RESULTS);
            setSelectedIndex(0);
            setActiveTab('all');
        } else {
            // Vider le cache quand on ferme (données peuvent devenir obsolètes)
            searchCache.current.clear();
        }
    }, [isOpen]);

    // Cache pour éviter les requêtes répétées
    const searchCache = useRef(new Map());
    const latestSearchKeyRef = useRef('');

    // Search API calls with debounce - Utilise le nouvel endpoint unifié optimisé
    useEffect(() => {
        if (!search || search.length < 2) {
            latestSearchKeyRef.current = '';
            setResults(EMPTY_RESULTS);
            setIsLoading(false);
            return;
        }

        // Vérifier le cache d'abord
        const cacheKey = search.toLowerCase().trim();
        if (searchCache.current.has(cacheKey)) {
            latestSearchKeyRef.current = cacheKey;
            setResults(searchCache.current.get(cacheKey));
            setIsLoading(false);
            setSelectedIndex(0);
            return;
        }

        setIsLoading(true);
        const timer = setTimeout(() => {
            latestSearchKeyRef.current = cacheKey;

            ApiDatabase.globalSearch(
                {
                    token,
                    search,
                    limit: 15,
                },
                (data) => {
                    if (latestSearchKeyRef.current !== cacheKey) return;

                    const newResults = {
                        missions: data.missions || [],
                        companies: data.companies || [],
                        clientContacts: data.clientContacts || [],
                        tempWorkers: data.tempWorkers || [],
                    };
                    // Stocker en cache (limiter à 50 entrées pour éviter memory leak)
                    if (searchCache.current.size > 50) {
                        const firstKey = searchCache.current.keys().next().value;
                        searchCache.current.delete(firstKey);
                    }
                    searchCache.current.set(cacheKey, newResults);
                    setResults(newResults);
                    setIsLoading(false);
                    setSelectedIndex(0);
                },
                (err) => {
                    if (err?.name === 'CanceledError') return;
                    if (latestSearchKeyRef.current !== cacheKey) return;

                    setResults(EMPTY_RESULTS);
                    setIsLoading(false);
                }
            );
        }, 300);

        return () => clearTimeout(timer);
    }, [search, token]);

    // Handle keyboard navigation
    const handleKeyDown = useCallback((e) => {
        if (!isOpen) return;

        switch (e.key) {
            case 'Escape':
                onClose();
                break;
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev => Math.min(prev + 1, allResults.length - 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => Math.max(prev - 1, 0));
                break;
            case 'Enter':
                e.preventDefault();
                if (allResults[selectedIndex]) {
                    handleSelectResult(allResults[selectedIndex]);
                }
                break;
            default:
                break;
        }
    }, [isOpen, allResults, selectedIndex, onClose]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    // Scroll selected item into view
    useEffect(() => {
        if (resultsRef.current) {
            const selectedElement = resultsRef.current.querySelector(`[data-index="${selectedIndex}"]`);
            if (selectedElement) {
                selectedElement.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [selectedIndex]);

    // Handle result selection
    const handleSelectResult = (result) => {
        onClose();
        
        switch (result.type) {
            case 'mission': {
                const href = buildMissionDetailHref(result);
                if (href) navigate(href);
                break;
            }
            case 'company':
                setObjID(result._id || result.id);
                setTarget('');
                toggleSlideOver('get_company');
                break;
            case 'clientContact': {
                const company = result.companies?.[0];
                if (company?._id) {
                    setObjID(company._id);
                    setTarget('members');
                    toggleSlideOver('get_company');
                }
                break;
            }
            case 'tempWorker':
                setObjID(result.id_user || result._id);
                toggleSlideOver('get_temp_worker');
                break;
            default:
                break;
        }
    };

    // Handle backdrop click
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) return null;

    const hasResults = allResults.length > 0;
    const isSearching = isLoading && search.length >= 2;
    const showEmptyState = search.length >= 2 && !isSearching && !hasResults;

    return ReactDOM.createPortal(
        <div 
            className="fixed inset-0 z-[99999] flex items-start justify-center pt-[15vh]"
            onClick={handleBackdropClick}
        >
            {/* Backdrop */}
            <div className="opacity-70 absolute inset-0 bg-gray-700 motion-safe:animate-veilIn" onClick={handleBackdropClick} />
            
            {/* Command Palette */}
            <div 
                className="relative z-10 w-full max-w-[600px] mx-4 bg-white rounded-xl shadow-2xl overflow-hidden motion-safe:animate-popIn"
            >
                {/* Search Input */}
                <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
                    <AdminSearchInput
                        ref={inputRef}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Rechercher par nom, numéro, contrat, email..."
                        variant="command"
                        isLoading={isLoading}
                        className="flex-1"
                    />
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                        <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-500 font-mono">esc</kbd>
                        <span>fermer</span>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex items-center gap-1 px-4 py-2 border-b border-gray-100">
                    {TABS.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => {
                                setActiveTab(tab.id);
                                setSelectedIndex(0);
                            }}
                            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                                activeTab === tab.id
                                    ? 'bg-gray-100 text-gray-700 font-medium'
                                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Results */}
                <div ref={resultsRef} className="max-h-[400px] overflow-y-auto">
                    {/* Missions */}
                    {!isSearching && filteredResults.missions.length > 0 && (
                        <div className="py-2">
                            {activeTab === 'all' && (
                                <div className="px-4 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Missions
                                </div>
                            )}
                            {filteredResults.missions.map((mission, index) => {
                                const match = buildMatchPresentation({
                                    search,
                                    item: mission,
                                    context: 'missionCommandPalette',
                                });
                                const subtitleParts = [];
                                if (mission.name_commercial) {
                                    subtitleParts.push(
                                        highlightSlot(mission.name_commercial, match.slotTerms, 'subtitle')
                                    );
                                }
                                if (mission.createdAt) {
                                    if (subtitleParts.length > 0) subtitleParts.push(' · ');
                                    subtitleParts.push(formatCreatedDate(mission.createdAt));
                                }
                                const assignedTempWorker = formatAssignedTempWorker(mission);
                                const missionBadge = mission.id_mission ? `N°${mission.id_mission}` : null;
                                const contractBadge = mission.contract_numbers?.length
                                    ? `Contrat N°${mission.contract_numbers.join(', ')}`
                                    : null;

                                return (
                                    <CommandPaletteItem
                                        key={`mission-${mission._id || mission.id_mission || index}`}
                                        dataIndex={index}
                                        isSelected={selectedIndex === index}
                                        icon={<MissionIcon wh={18} color="#6B7280" />}
                                        title={mission.title || mission.name || 'Mission'}
                                        subtitle={subtitleParts.length > 0 ? subtitleParts : null}
                                        extraSubtitle={assignedTempWorker
                                            ? highlightSlot(assignedTempWorker, match.slotTerms, 'extraSubtitle', {
                                                fuzzy: true,
                                                searchTerms: match.terms,
                                              })
                                            : null}
                                        matchHints={match.matchHints}
                                        badge={missionBadge
                                            ? highlightSlot(missionBadge, match.slotTerms, 'badge')
                                            : null}
                                        secondBadge={contractBadge
                                            ? highlightSlot(contractBadge, match.slotTerms, 'secondBadge')
                                            : null}
                                        onClick={() => handleSelectResult({ ...mission, type: 'mission' })}
                                        onMouseEnter={() => setSelectedIndex(index)}
                                    />
                                );
                            })}
                        </div>
                    )}

                    {/* Companies */}
                    {!isSearching && filteredResults.companies.length > 0 && (
                        <div className="py-2">
                            {activeTab === 'all' && (
                                <div className="px-4 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Entreprises
                                </div>
                            )}
                            {filteredResults.companies.map((company, index) => {
                                const itemIndex = filteredResults.missions.length + index;
                                const match = buildMatchPresentation({
                                    search,
                                    item: company,
                                    context: 'company',
                                });
                                const companyTitle = company.name_commercial || company.name || 'Entreprise';
                                const companySubtitle = company.siret
                                    ? `SIRET: ${company.siret}`
                                    : company.address?.city || '';
                                const companyBadge = company.id_bestt ? `N°${company.id_bestt}` : null;

                                return (
                                    <CommandPaletteItem
                                        key={`company-${company._id || company.id || index}`}
                                        dataIndex={itemIndex}
                                        isSelected={selectedIndex === itemIndex}
                                        icon={<Building wh={18} color="#6B7280" />}
                                        title={highlightSlot(companyTitle, match.slotTerms, 'title', { fuzzy: true })}
                                        subtitle={companySubtitle
                                            ? highlightSlot(companySubtitle, match.slotTerms, 'subtitle')
                                            : null}
                                        matchHints={match.matchHints}
                                        badge={companyBadge
                                            ? highlightSlot(companyBadge, match.slotTerms, 'badge')
                                            : null}
                                        onClick={() => handleSelectResult({ ...company, type: 'company' })}
                                        onMouseEnter={() => setSelectedIndex(itemIndex)}
                                    />
                                );
                            })}
                        </div>
                    )}

                    {/* Contact client */}
                    {!isSearching && filteredResults.clientContacts.length > 0 && (
                        <div className="py-2">
                            {activeTab === 'all' && (
                                <div className="px-4 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Contact client
                                </div>
                            )}
                            {filteredResults.clientContacts.map((contact, index) => {
                                const itemIndex = filteredResults.missions.length + filteredResults.companies.length + index;
                                const match = buildMatchPresentation({
                                    search,
                                    item: contact,
                                    context: 'clientContact',
                                });
                                const contactTitle = `${contact.firstname || ''} ${contact.lastname || ''}`.trim() || 'Contact client';
                                const subtitleParts = [];
                                if (contact.email) {
                                    subtitleParts.push(highlightSlot(contact.email, match.slotTerms, 'subtitle'));
                                }
                                if (contact.phone) {
                                    if (subtitleParts.length > 0) subtitleParts.push(' · ');
                                    subtitleParts.push(highlightSlot(contact.phone, match.slotTerms, 'subtitle'));
                                }
                                const companyNames = formatClientContactCompanies(contact);

                                return (
                                    <CommandPaletteItem
                                        key={`client-contact-${contact._id || contact.id_user || index}`}
                                        dataIndex={itemIndex}
                                        isSelected={selectedIndex === itemIndex}
                                        icon={<PersonIcon wh={18} color="#6B7280" />}
                                        title={highlightSlot(contactTitle, match.slotTerms, 'title', { fuzzy: true })}
                                        subtitle={subtitleParts.length > 0 ? subtitleParts : null}
                                        matchHints={match.matchHints}
                                        badge={companyNames
                                            ? highlightSlot(companyNames, match.slotTerms, 'badge')
                                            : null}
                                        onClick={() => handleSelectResult({ ...contact, type: 'clientContact' })}
                                        onMouseEnter={() => setSelectedIndex(itemIndex)}
                                    />
                                );
                            })}
                        </div>
                    )}

                    {/* Candidats */}
                    {!isSearching && filteredResults.tempWorkers.length > 0 && (
                        <div className="py-2">
                            {activeTab === 'all' && (
                                <div className="px-4 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Candidats
                                </div>
                            )}
                            {filteredResults.tempWorkers.map((tempWorker, index) => {
                                const itemIndex = filteredResults.missions.length + filteredResults.companies.length + filteredResults.clientContacts.length + index;
                                const match = buildMatchPresentation({
                                    search,
                                    item: tempWorker,
                                    context: 'tempWorker',
                                });
                                const tempWorkerTitle = `${tempWorker.firstname || ''} ${tempWorker.lastname || ''}`.trim() || 'Candidat';
                                const subtitleParts = [];
                                if (tempWorker.email) {
                                    subtitleParts.push(highlightSlot(tempWorker.email, match.slotTerms, 'subtitle'));
                                }
                                if (tempWorker.phone) {
                                    if (subtitleParts.length > 0) subtitleParts.push(' · ');
                                    subtitleParts.push(highlightSlot(tempWorker.phone, match.slotTerms, 'subtitle'));
                                }
                                const tempWorkerBadge = tempWorker.id_bestt ? `N°${tempWorker.id_bestt}` : null;

                                return (
                                    <CommandPaletteItem
                                        key={`tempworker-${tempWorker._id || tempWorker.id_user || index}`}
                                        dataIndex={itemIndex}
                                        isSelected={selectedIndex === itemIndex}
                                        icon={<PersonIcon wh={18} color="#6B7280" />}
                                        title={highlightSlot(tempWorkerTitle, match.slotTerms, 'title', { fuzzy: true })}
                                        subtitle={subtitleParts.length > 0 ? subtitleParts : null}
                                        matchHints={match.matchHints}
                                        badge={tempWorkerBadge
                                            ? highlightSlot(tempWorkerBadge, match.slotTerms, 'badge')
                                            : null}
                                        onClick={() => handleSelectResult({ ...tempWorker, type: 'tempWorker' })}
                                        onMouseEnter={() => setSelectedIndex(itemIndex)}
                                    />
                                );
                            })}
                        </div>
                    )}

                    {/* Loading State */}
                    {isSearching && (
                        <div className="py-12 flex justify-center">
                            <div className="animate-spin">
                                <Loading2 wh={32} color="#FF9600" />
                            </div>
                        </div>
                    )}

                    {/* Empty State */}
                    {showEmptyState && (
                        <div className="flex flex-col items-center justify-center py-16 px-4">
                            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                                <SearchLoop wh={32} color="#9CA3AF" />
                            </div>
                            <div className="text-gray-900 font-semibold text-lg mb-2">Aucun résultat</div>
                            <div className="text-gray-500 text-sm text-center max-w-md">
                                Aucun résultat trouvé pour "{search}"
                            </div>
                        </div>
                    )}

                    {/* Initial State */}
                    {search.length < 2 && (
                        <div className="py-8 text-center">
                            <p className="text-sm text-gray-400">Tapez au moins 2 caractères pour rechercher</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 bg-gray-50 text-xs text-gray-500">
                    <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1">
                            <kbd className="px-1.5 py-0.5 bg-white rounded border border-gray-200 font-mono">↑</kbd>
                            <kbd className="px-1.5 py-0.5 bg-white rounded border border-gray-200 font-mono">↓</kbd>
                            <span>naviguer</span>
                        </span>
                        <span className="flex items-center gap-1">
                            <kbd className="px-1.5 py-0.5 bg-white rounded border border-gray-200 font-mono">↵</kbd>
                            <span>sélectionner</span>
                        </span>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default CommandPalette;
